import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CalendarClock, Calendar as CalendarIcon, Users, Tag,
  Contact, BarChart3, Settings, LogOut, ChevronDown, ListChecks, Dumbbell, Award,
  PanelLeftClose, PanelLeftOpen, Download, PhoneCall,
} from "lucide-react";
import { ROLE_LABEL } from "@/utils/status";

const NAV_CONSULENTI = [
  { to: "/calendario", label: "Cal. Consulenti", icon: CalendarIcon, testid: "nav-calendar" },
  { to: "/flusso-lavoro", label: "Flusso Consulenti", icon: ListChecks, testid: "nav-workflow" },
];
const NAV_TRAINERS = [
  { to: "/calendario-trainers", label: "Cal. Trainers", icon: Dumbbell, testid: "nav-calendar-trainers" },
  { to: "/flusso-trainers", label: "Flusso Trainers", icon: ListChecks, testid: "nav-workflow-trainers" },
];
const STAFF = ["admin", "manager", "receptionist"];
const NAV_CLIENTS = [
  { to: "/promemoria", label: "Chiamate Clienti", icon: PhoneCall, testid: "nav-reminders", roles: STAFF },
  { to: "/clienti", label: "Clienti", icon: Contact, testid: "nav-clients" },
];
const NAV_ANALYSIS = [
  { to: "/statistiche", label: "Statistiche", icon: BarChart3, testid: "nav-stats" },
  { to: "/esportazione", label: "Esportazione", icon: Download, testid: "nav-export", roles: STAFF },
];
const NAV_CONFIG = [
  { to: "/collaboratori", label: "Team", icon: Users, testid: "nav-collaborators", roles: ["admin", "manager"] },
  { to: "/tipi-appuntamento", label: "Tipi Appuntamento", icon: Tag, testid: "nav-types", roles: ["admin"] },
  { to: "/esiti", label: "Stati & Esiti", icon: Award, testid: "nav-outcomes", roles: ["admin"] },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-settings" },
];
const forRole = (items, role) => items.filter((i) => !i.roles || i.roles.includes(role));

const STORAGE_KEY = "meridiana_sidebar_collapsed";

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const showConsulenti = user?.role !== "trainer";
  const role = user?.role;

  const toggle = () => setCollapsed((v) => !v);

  return (
    <div className="h-screen flex bg-background overflow-hidden print:h-auto print:block print:overflow-visible print:bg-white">
      <aside
        className={`print:hidden shrink-0 border-r bg-slate-950 text-slate-100 flex flex-col transition-[width] duration-200 ease-out ${collapsed ? "w-16" : "w-64"}`}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        <div className={`border-b border-slate-800 flex items-center ${collapsed ? "justify-center px-2 py-4" : "gap-3 px-5 py-5"}`}>
          <div className="h-9 w-9 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
            <CalendarClock className="h-4 w-4" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-display font-bold text-base tracking-tight">Meridiana</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Booking Suite</div>
            </div>
          )}
        </div>

        <nav className={`flex-1 overflow-y-auto py-4 space-y-3 ${collapsed ? "px-2" : "px-3"}`}>
          {showConsulenti && <NavSection title="Consulenti" items={NAV_CONSULENTI} collapsed={collapsed} />}
          <NavSection title="Trainers" items={NAV_TRAINERS} collapsed={collapsed} />
          <NavSection title="Clienti" items={forRole(NAV_CLIENTS, role)} collapsed={collapsed} />
          <NavSection title="Analisi" items={forRole(NAV_ANALYSIS, role)} collapsed={collapsed} />
          <NavSection title="Configurazione" items={forRole(NAV_CONFIG, role)} collapsed={collapsed} />
        </nav>

        <div className="border-t border-slate-800 p-2 space-y-1">
          <button
            onClick={toggle}
            data-testid="sidebar-toggle-button"
            title={collapsed ? "Espandi menu" : "Riduci menu"}
            className={`w-full flex items-center rounded-md text-slate-300 hover:bg-slate-800 hover:text-white transition-colors ${
              collapsed ? "justify-center h-10" : "gap-3 px-3 py-2 text-sm"
            }`}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            {!collapsed && <span>Riduci menu</span>}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`w-full flex items-center rounded-md hover:bg-slate-800 text-left ${collapsed ? "justify-center p-2" : "gap-3 px-2 py-2"}`}
                data-testid="user-menu-trigger"
                title={collapsed ? `${user?.name || user?.email} · ${ROLE_LABEL[user?.role] || ""}` : undefined}
              >
                <div
                  className="h-9 w-9 rounded-full flex items-center justify-center font-semibold text-xs text-white shrink-0"
                  style={{ backgroundColor: user?.color || "#4F46E5" }}
                >
                  {initials}
                </div>
                {!collapsed && (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
                      <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex items-center justify-between">
                  <span>{user?.name}</span>
                  <Badge variant="secondary" data-testid="user-role-badge">{ROLE_LABEL[user?.role]}</Badge>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/impostazioni")} data-testid="menu-settings">
                <Settings className="h-4 w-4 mr-2" /> Impostazioni
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex-1 overflow-hidden print:overflow-visible">
        <Outlet />
      </main>
    </div>
  );
}

function NavSection({ title, items, collapsed }) {
  return (
    <div>
      {title && !collapsed && (
        <div className="px-2 mb-1 text-[10px] uppercase tracking-widest font-bold text-slate-500">
          {title}
        </div>
      )}
      {title && collapsed && (
        <div className="h-px mx-2 my-2 bg-slate-800" />
      )}
      <div className="space-y-0.5">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `flex items-center rounded-md text-sm transition-colors ${collapsed ? "justify-center h-10" : "gap-3 px-3 py-2"} ${
                  isActive
                    ? "bg-blue-600 text-white font-semibold"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}
